import { Flex, Text, SystemStyleObject } from '@chakra-ui/react';
import { FC, Fragment } from 'react';
import useBreakpoint from 'hooks/useBreakpoint';
import AnimatedDigit from './AnimatedDigit';

interface Props {
  number: number | string;
  digitFontSize: string;
  digitStyleOverride?: { desktop?: SystemStyleObject; mobile?: SystemStyleObject };
  isCorporateTheme?: boolean;
}

function splitToDigits(number: string): number[] {
  const digits = number.split('').map(n => Number(n));

  return digits;
}

function extractAbbrev(number: string): { abbrev: string; number: string } {
  const lastCharacter = number.substr(number.length - 1);
  const abbrev = Number.isNaN(Number(lastCharacter)) ? lastCharacter : '';

  return {
    abbrev,
    number: abbrev ? number.slice(0, -1) : number,
  };
}

function splitDecimals(number: string): { integrals: string; decimals?: string } {
  let integrals;
  let decimals;
  if (number.includes('.')) {
    const delimeterIndex = number.indexOf('.');
    integrals = number.slice(0, delimeterIndex);
    decimals = number.slice(delimeterIndex + 1);
  } else {
    integrals = number;
  }

  return {
    integrals,
    decimals,
  };
}

const AnimatedNumbers: FC<Props> = ({ number, digitFontSize, digitStyleOverride, isCorporateTheme }) => {
  const { isMobile } = useBreakpoint();

  let numberToDisplay;
  let decimalsToDisplay;
  let abbreviation;

  if (typeof number === 'string') {
    const { abbrev, number: numberWithoutAbbrev } = extractAbbrev(number);
    const { integrals, decimals } = splitDecimals(numberWithoutAbbrev);
    numberToDisplay = integrals;
    decimalsToDisplay = decimals;
    abbreviation = abbrev;
  } else {
    numberToDisplay = number.toString();
  }

  const digits = splitToDigits(numberToDisplay).reverse();
  const decimalDigits = decimalsToDisplay ? splitToDigits(decimalsToDisplay) : [];

  const numbers = [];
  const decimals = [];

  for (let i = 0; i < digits.length; i++) {
    numbers.push(
      <Fragment key={i}>
        <AnimatedDigit digit={digits[i]} fontSize={digitFontSize} sx={isMobile ? digitStyleOverride?.mobile : digitStyleOverride?.desktop} />
        {i !== 0 && i % 3 === 0 ? (
          <Text fontSize={digitFontSize} fontWeight="bold" sx={isMobile ? digitStyleOverride?.mobile : digitStyleOverride?.desktop}>
            ,
          </Text>
        ) : null}
      </Fragment>,
    );
  }

  for (let i = 0; i < decimalDigits.length; i++) {
    decimals.push(
      <Fragment key={i}>
        <AnimatedDigit digit={decimalDigits[i]} fontSize={digitFontSize} sx={isMobile ? digitStyleOverride?.mobile : digitStyleOverride?.desktop} />
      </Fragment>,
    );
  }

  return (
    <Flex direction="row" alignItems="flex-end" letterSpacing={isCorporateTheme ? '0px' : '1px'}>
      {numbers.reverse()}
      {decimals.length ? (
        <Text fontSize={digitFontSize} fontWeight="300" sx={isMobile ? digitStyleOverride?.mobile : digitStyleOverride?.desktop}>
          .
        </Text>
      ) : null}
      {decimals}
      <Text fontSize={digitFontSize} fontWeight="300" sx={isMobile ? digitStyleOverride?.mobile : digitStyleOverride?.desktop}>
        {abbreviation}
      </Text>
    </Flex>
  );
};

export default AnimatedNumbers;
